/**
 * ============================================================================
 * KpiTable — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: cross-supplier KPI grid. One row per supplier, 8 KPI columns
 *          plus aggregate score and risk badge. Sortable on every column.
 *
 * A11Y: <table> with caption + <th scope="col">; sort headers expose
 *  aria-sort (ascending/descending/none) and an aria-pressed toggle.
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './SupplierKpiDashboard.module.css';
import type { KpiDashboardRow } from './useKpiDashboardQuery';
import type { SupplierKpiKey } from '../SupplierDetail/useSupplierDetailQuery';
import type { RiskCategory } from '../SupplierWorkbench/useSuppliersQuery';

const KPI_KEYS: SupplierKpiKey[] = [
  'OTIF',
  'DELIVERY_PERFORMANCE',
  'DEVIATION_COUNT',
  'COMPLAINT_COUNT',
  'OOS_RATE',
  'AUDIT_SCORE',
  'CAPA_EFFECTIVENESS',
  'QUALITY_INCIDENTS',
];

type SortKey = 'name' | 'aggregate' | SupplierKpiKey;
type SortDir = 'asc' | 'desc';

const RISK_CLASS: Record<RiskCategory, string> = {
  CRITICAL: styles.riskCritical,
  MAJOR: styles.riskMajor,
  MINOR: styles.riskMinor,
  LOW: styles.riskLow,
};

const BAND_DOT: Record<'GREEN' | 'AMBER' | 'RED' | 'UNKNOWN', string> = {
  GREEN: styles.bandGreen,
  AMBER: styles.bandAmber,
  RED: styles.bandRed,
  UNKNOWN: styles.bandUnknown,
};

export interface KpiTableProps {
  rows: KpiDashboardRow[];
  onOpenSupplier: (supplierId: string) => void;
}

export function KpiTable({ rows, onOpenSupplier }: KpiTableProps) {
  const { t, i18n } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  function getSortValue(row: KpiDashboardRow, key: SortKey): number | string {
    if (key === 'name') return row.name;
    if (key === 'aggregate') return row.aggregate_quality_score ?? -Infinity;
    return row.kpis[key]?.value ?? -Infinity;
  }

  const sorted = useMemo(() => {
    const next = [...rows];
    next.sort((a, b) => {
      const av = getSortValue(a, sortKey);
      const bv = getSortValue(b, sortKey);
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else {
        cmp = String(av).localeCompare(String(bv), i18n.language);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return next;
  }, [rows, sortKey, sortDir, i18n.language]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function ariaSort(key: SortKey): 'ascending' | 'descending' | 'none' {
    if (key !== sortKey) return 'none';
    return sortDir === 'asc' ? 'ascending' : 'descending';
  }

  return (
    <table
      className={wstyles.table}
      aria-label={t('supplier.kpi.tableAria', {
        defaultValue: 'Cross-supplier KPI grid',
      })}
    >
      <caption className={styles.srOnly}>
        {t('supplier.kpi.tableCaption', {
          defaultValue:
            'One row per supplier with eight KPI columns, an aggregate quality score, and the current risk category.',
        })}
      </caption>
      <thead>
        <tr>
          <th scope="col" aria-sort={ariaSort('name')}>
            <SortBtn
              active={sortKey === 'name'}
              dir={sortDir}
              onClick={() => toggleSort('name')}
              label={t('supplier.kpi.columns.name', {
                defaultValue: 'Supplier',
              })}
            />
          </th>
          {KPI_KEYS.map((k) => (
            <th key={k} scope="col" aria-sort={ariaSort(k)}>
              <SortBtn
                active={sortKey === k}
                dir={sortDir}
                onClick={() => toggleSort(k)}
                label={t(`supplier.detail.kpi.${k}.label`, { defaultValue: k })}
              />
            </th>
          ))}
          <th scope="col" aria-sort={ariaSort('aggregate')}>
            <SortBtn
              active={sortKey === 'aggregate'}
              dir={sortDir}
              onClick={() => toggleSort('aggregate')}
              label={t('supplier.kpi.columns.aggregate', {
                defaultValue: 'Aggregate score',
              })}
            />
          </th>
          <th scope="col">
            {t('supplier.kpi.columns.risk', { defaultValue: 'Risk' })}
          </th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((row) => (
          <tr key={row.supplier_id}>
            <td>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => onOpenSupplier(row.supplier_id)}
                aria-label={t('supplier.kpi.openAria', {
                  defaultValue: 'Open dossier for {{name}}',
                  name: row.name,
                })}
              >
                <strong>{row.name}</strong>
              </button>
            </td>
            {KPI_KEYS.map((k) => {
              const cell = row.kpis[k];
              return (
                <td key={k}>
                  <KpiCell cell={cell} />
                </td>
              );
            })}
            <td>
              {row.aggregate_quality_score == null ? (
                <span className={styles.subdued}>—</span>
              ) : (
                <span className={styles.aggregateScore}>
                  {row.aggregate_quality_score}
                </span>
              )}
            </td>
            <td>
              {row.risk_category ? (
                <span
                  className={`${styles.riskBadge} ${RISK_CLASS[row.risk_category]}`}
                  aria-label={t('supplier.kpi.riskAria', {
                    defaultValue: 'Risk category: {{c}}',
                    c: t(`supplier.risk.${row.risk_category}`, {
                      defaultValue: row.risk_category,
                    }),
                  })}
                >
                  {t(`supplier.risk.${row.risk_category}`, {
                    defaultValue: row.risk_category,
                  })}
                </span>
              ) : (
                <span className={styles.subdued}>—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ----------------------------------------------------------------------------
 * KpiCell — value + band dot. Both colour and text label appear so colour
 *  is never the sole signal (WCAG 1.4.1).
 * --------------------------------------------------------------------------- */

function KpiCell({ cell }: { cell: import('./useKpiDashboardQuery').KpiCellValue | undefined }) {
  const { t, i18n } = useTranslation();
  if (!cell) return <span className={styles.subdued}>—</span>;
  if (cell.value == null) return <span className={styles.subdued}>—</span>;
  const nf = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });
  const bandLabel = t(`supplier.kpi.band.${cell.band}`, {
    defaultValue: cell.band,
  });
  return (
    <span className={styles.kpiCell}>
      <span
        className={`${styles.dot} ${BAND_DOT[cell.band]}`}
        aria-hidden="true"
      />
      <span
        className={styles.kpiCellValue}
        aria-label={`${nf.format(cell.value)} (${bandLabel})`}
      >
        {nf.format(cell.value)}
      </span>
    </span>
  );
}

/* ----------------------------------------------------------------------------
 * SortBtn — sortable column header button.
 * --------------------------------------------------------------------------- */

interface SortBtnProps {
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  label: string;
}

function SortBtn({ active, dir, onClick, label }: SortBtnProps) {
  return (
    <button
      type="button"
      className={styles.sortBtn}
      onClick={onClick}
      aria-pressed={active}
    >
      <span>{label}</span>
      <span aria-hidden="true" className={styles.sortIndicator}>
        {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </button>
  );
}
